import { 
  Search, 
  Bell, 
  Sparkles, 
  User,
  ChevronDown,
  Plus,
  Upload,
  FileText,
  Scan,
  LogOut
} from 'lucide-react'
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { useAppStore } from '@/stores/appStore'
import { useAuthStore } from '@/stores/authStore'
import type { ViewType } from '@/pages/dashboard/DashboardApp'

interface HeaderProps {
  currentView: ViewType
  onToggleCopilot: () => void
  isCopilotOpen: boolean
  onNavigate?: (view: ViewType) => void
}

const viewTitles: Record<ViewType, string> = {
  dashboard: 'Dashboard',
  projects: 'Projects',
  'project-details': 'Project Details',
  'text-to-3d': 'Text to 3D',
  design: 'Design Studio',
  upload: 'Upload Design',
  printers: 'Printer Management',
  print: 'Print Center',
  marketplace: 'MakerHub',
  settings: 'Settings',
}

const createMenuItems = [
  { id: 'new', label: 'New Design', icon: Plus, shortcut: '⌘N' },
  { id: 'import', label: 'Import File', icon: Upload, shortcut: '⌘I' },
  { id: 'template', label: 'From Template', icon: FileText },
  { id: 'scan', label: 'Scan to CAD', icon: Scan, badge: 'AI' },
]

export default function Header({ currentView, onToggleCopilot, isCopilotOpen, onNavigate }: HeaderProps) {
  const { notifications } = useAppStore()
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const [showCreateMenu, setShowCreateMenu] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  
  const unreadCount = notifications.filter(n => !n.read).length

  const handleLogout = () => {
    logout()
    navigate({ to: '/' })
  }

  const handleCreateAction = (actionId: string) => {
    setShowCreateMenu(false)
    switch (actionId) {
      case 'import':
        onNavigate?.('upload')
        break
      case 'new':
        onNavigate?.('design')
        break
      case 'scan':
        onNavigate?.('design')
        break
      default:
        break
    }
  }

  return (
    <header className="h-16 bg-surface-900/80 backdrop-blur-xl border-b border-surface-800 flex items-center justify-between px-6 z-10">
      {/* Left Section */}
      <div className="flex items-center gap-6">
        <h2 className="text-lg font-semibold text-white">
          {viewTitles[currentView]}
        </h2>
        
        {/* Create Button */}
        <div className="relative">
          <button
            onClick={() => setShowCreateMenu(!showCreateMenu)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Create</span>
            <ChevronDown className="w-4 h-4" />
          </button>
          
          <AnimatePresence>
            {showCreateMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowCreateMenu(false)} 
                />
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 mt-2 w-56 bg-surface-800 border border-surface-700 rounded-xl shadow-xl overflow-hidden z-20"
                >
                  {createMenuItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleCreateAction(item.id)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-700 transition-colors"
                    >
                      <item.icon className="w-4 h-4 text-surface-400" />
                      <span className="text-sm text-surface-200 flex-1 text-left">{item.label}</span>
                      {item.shortcut && (
                        <span className="text-xs text-surface-500">{item.shortcut}</span>
                      )}
                      {item.badge && (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold bg-ai-500/20 text-ai-400 rounded">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Center - Search */}
      <div className="flex-1 max-w-xl mx-8">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
          <input
            type="text"
            placeholder="Search designs, models, or ask AI..."
            className="w-full h-10 pl-11 pr-4 bg-surface-800 border border-surface-700 rounded-xl text-sm text-surface-200 placeholder:text-surface-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-surface-500">
            <kbd className="px-1.5 py-0.5 bg-surface-700 rounded">⌘</kbd>
            <kbd className="px-1.5 py-0.5 bg-surface-700 rounded">K</kbd>
          </div>
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-3">
        {/* AI Copilot Toggle */}
        <button
          onClick={onToggleCopilot}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all',
            isCopilotOpen 
              ? 'bg-ai-500/20 text-ai-400 ai-glow' 
              : 'bg-surface-800 text-surface-300 hover:bg-surface-700 hover:text-ai-400'
          )}
        >
          <Sparkles className="w-4 h-4" />
          <span className="text-sm">AI Copilot</span>
        </button>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2.5 rounded-lg bg-surface-800 hover:bg-surface-700 transition-colors"
          >
            <Bell className="w-5 h-5 text-surface-300" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-error-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>

          <AnimatePresence>
            {showNotifications && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowNotifications(false)} 
                />
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full right-0 mt-2 w-80 bg-surface-800 border border-surface-700 rounded-xl shadow-xl overflow-hidden z-20"
                >
                  <div className="px-4 py-3 border-b border-surface-700">
                    <h3 className="font-semibold text-surface-100">Notifications</h3>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-surface-400 text-sm">
                        No notifications
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={cn(
                            'px-4 py-3 border-b border-surface-700/50 hover:bg-surface-700/50 transition-colors',
                            !notification.read && 'bg-surface-700/30'
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <div className={cn(
                              'w-2 h-2 rounded-full mt-1.5 flex-shrink-0',
                              notification.type === 'success' && 'bg-success-500',
                              notification.type === 'error' && 'bg-error-500',
                              notification.type === 'warning' && 'bg-warning-500',
                              notification.type === 'info' && 'bg-primary-500'
                            )} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-surface-200">{notification.title}</p>
                              <p className="text-xs text-surface-400 mt-0.5">{notification.message}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* User Menu */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-800 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-ai-500 flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
            <div className="text-left hidden lg:block">
              <p className="text-sm font-medium text-surface-200">{user?.name}</p>
              <p className="text-xs text-surface-400 capitalize">{user?.plan} Plan</p>
            </div>
            <ChevronDown className="w-4 h-4 text-surface-400 hidden lg:block" />
          </button>

          <AnimatePresence>
            {showUserMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowUserMenu(false)} 
                />
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full right-0 mt-2 w-56 bg-surface-800 border border-surface-700 rounded-xl shadow-xl overflow-hidden z-20"
                >
                  <div className="px-4 py-3 border-b border-surface-700">
                    <p className="font-medium text-surface-200">{user?.name}</p>
                    <p className="text-sm text-surface-400">{user?.email}</p>
                  </div>
                  <div className="py-1">
                    <button className="w-full px-4 py-2.5 text-left text-sm text-surface-300 hover:bg-surface-700">Profile</button>
                    <button className="w-full px-4 py-2.5 text-left text-sm text-surface-300 hover:bg-surface-700">Billing</button>
                    <button className="w-full px-4 py-2.5 text-left text-sm text-surface-300 hover:bg-surface-700">Preferences</button>
                  </div>
                  <div className="py-1 border-t border-surface-700">
                    <button 
                      onClick={handleLogout}
                      className="w-full px-4 py-2.5 text-left text-sm text-error-400 hover:bg-surface-700 flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  )
}
